import { useCallback, useLayoutEffect, useState, type RefObject } from "react"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import { SelectDropdown, type SelectDropdownOption } from "@/components/ui/select-dropdown"
import { Button } from "@/components/ui/primitives"
import { EntityNameEditDialog } from "@/components/workspace/entity-name-edit-dialog"
import { useTranslation } from "@/hooks/use-translation"
import {
  cameraPathTakesFull,
  nextCameraPathTakeNumber,
  type CameraPathTakeSet,
} from "@/lib/location-scene-3d/camera-path-takes"
import type { CameraPath } from "@/lib/location-scene-3d/camera-path"
import type { Scene3dEngine } from "@/lib/location-scene-3d/engine"

type CameraPathTakeSwitcherProps = {
  engineRef: RefObject<Scene3dEngine | null>
  cameraPath: CameraPath
  canEdit: boolean
  /** Запись и сохранение превиза: селект и «+» выключены. */
  disabled: boolean
}

export function CameraPathTakeSwitcher({ engineRef, cameraPath, canEdit, disabled }: CameraPathTakeSwitcherProps) {
  const { t } = useTranslation(["workspace", "common"])
  const [set, setSet] = useState<CameraPathTakeSet>({ takes: [], activeId: null })
  const [renameId, setRenameId] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const refresh = useCallback(() => {
    const next = engineRef.current?.getCameraPathTakes()
    if (next) setSet(next)
  }, [engineRef])

  useLayoutEffect(() => {
    refresh()
  }, [cameraPath, refresh])

  const renameTake = set.takes.find((take) => take.id === renameId) ?? null
  const deleteTake = set.takes.find((take) => take.id === deleteId) ?? null

  const addTake = () => {
    const engine = engineRef.current
    if (!engine || !canEdit || disabled) return
    const current = engine.getCameraPathTakes()
    if (cameraPathTakesFull(current.takes)) {
      toast.error(t("locationsPage.scene3d.timeline.takesFull"))
      return
    }
    const name = t("locationsPage.scene3d.timeline.takeName", { n: nextCameraPathTakeNumber(current.takes) })
    engine.addCameraPathTake(name)
    refresh()
  }

  const options: SelectDropdownOption[] = set.takes.map((take) => ({
    value: take.id,
    label: take.name,
    actions: canEdit
      ? [
          {
            icon: <Pencil className="h-3 w-3" />,
            ariaLabel: t("locationsPage.scene3d.timeline.renameTake"),
            className:
              "inline-flex items-center justify-center rounded-sm p-1 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary",
            onSelect: () => setRenameId(take.id),
          },
          {
            icon: <Trash2 className="h-3 w-3" />,
            ariaLabel: t("locationsPage.scene3d.timeline.deleteTake"),
            className:
              "inline-flex items-center justify-center rounded-sm p-1 text-ui-text-muted hover:bg-ui-status-failed/15 hover:text-ui-status-failed",
            onSelect: () => setDeleteId(take.id),
          },
        ]
      : undefined,
  }))

  return (
    <>
      <div className="flex h-full w-full min-w-0 items-center gap-1">
        <SelectDropdown
          value={set.activeId ?? ""}
          onChange={(id) => {
            engineRef.current?.selectCameraPathTake(id)
            refresh()
          }}
          options={options}
          disabled={disabled || options.length === 0}
          dropdownDirection="up"
          menuAlign="left"
          showSelectedCheck
          actionsOnHover
          ariaLabel={t("locationsPage.scene3d.timeline.takes")}
          placeholder={t("locationsPage.scene3d.timeline.takes")}
          size="xs"
          className="flex min-w-0 flex-1"
          buttonClassName="min-w-0"
        />
        {canEdit ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 w-6 shrink-0 rounded-md p-0"
            disabled={disabled}
            title={t("locationsPage.scene3d.timeline.newTake")}
            aria-label={t("locationsPage.scene3d.timeline.newTake")}
            onClick={addTake}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
      <EntityNameEditDialog
        open={renameTake != null}
        onOpenChange={(open) => {
          if (!open) setRenameId(null)
        }}
        title={t("locationsPage.scene3d.timeline.renameTake")}
        nameLabel={t("locationsPage.scene3d.timeline.renameTakeName")}
        initialName={renameTake?.name ?? ""}
        maxLength={120}
        nested={3}
        closeAriaLabel={t("close", { ns: "common" })}
        cancelLabel={t("cancel", { ns: "common" })}
        saveLabel={t("save", { ns: "common" })}
        onSubmit={async (name) => {
          if (!renameTake) return
          engineRef.current?.renameCameraPathTake(renameTake.id, name)
          setRenameId(null)
          refresh()
        }}
      />
      <ConfirmActionDialog
        open={deleteTake != null}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null)
        }}
        tone="danger"
        title={t("locationsPage.scene3d.timeline.deleteTake")}
        message={t("locationsPage.scene3d.timeline.deleteTakeConfirm", { name: deleteTake?.name ?? "" })}
        confirmLabel={t("locationsPage.scene3d.timeline.deleteTakeAction")}
        onConfirm={() => {
          if (!deleteTake) return
          engineRef.current?.deleteCameraPathTake(deleteTake.id)
          setDeleteId(null)
          refresh()
        }}
      />
    </>
  )
}
